/**
 * selectSla — redux read seam for the desktop Delivery SLA UI.
 *
 * `fetchDeliveryDetailsV2` (src/edd/actions) stores the checkDeliveryDetails v2
 * response as `edd.slaDetails[pinCode][skuId] = { options, isFetching, error }`.
 * The response can be keyed by a code other than the requested one, so the
 * reducer settles both keys on SUCCESS — which means the lookup here can (and
 * must) stay strictly scoped to the SKU asked for. Reading any populated entry
 * for the pincode instead would surface another size's / product's SLA: stale
 * cards while a new size is in flight, and cards for a product with no size
 * selected at all.
 *
 * Single place the desktop UI touches the slice, so components stay unaware of
 * the store shape. Reads are defensive: nothing exists until a fetch resolves.
 */
import { RawSlaOption } from './types';

interface SlaSliceEntry {
  options?: RawSlaOption[];
  isFetching?: boolean;
  error?: boolean;
}

/** Entry for exactly this pincode + SKU. No cross-SKU fallback — see above. */
const getEntry = (state: any, pincode?: string, skuId?: string): SlaSliceEntry | undefined => {
  if (!pincode || !skuId) return undefined;
  return state?.edd?.slaDetails?.[pincode]?.[skuId];
};

/** Raw SLA options for the given pincode + SKU (empty until the fetch lands). */
export const selectSlaOptions = (state: any, pincode?: string, skuId?: string): RawSlaOption[] =>
  getEntry(state, pincode, skuId)?.options || [];

/** Per-SKU fetching flag, falling back to the slice-wide one. */
export const selectIsSlaFetching = (state: any, pincode?: string, skuId?: string): boolean => {
  const entry = getEntry(state, pincode, skuId);
  if (entry && typeof entry.isFetching === 'boolean') return entry.isFetching;
  return Boolean(state?.edd?.isSlaFetching);
};

export const selectSlaError = (state: any, pincode?: string, skuId?: string): boolean =>
  Boolean(getEntry(state, pincode, skuId)?.error);
